import {
    Component,
    forwardRef,
    input,
    InputSignal,
    ViewChild,
    ElementRef,
    effect,
    CUSTOM_ELEMENTS_SCHEMA,
    signal,
    WritableSignal,
    computed,
    Signal,
    inject
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TeamService } from '../../services/team.service';
import { BusinessServices, ITeam } from '@shs-digi-care-monorepo/interfaces';
import { of, catchError, tap, finalize, filter } from 'rxjs';
import { CommonModule } from '@angular/common';

/**
 * Team picker component with search functionality.
 * Exposes team ID as value and provides editable dropdown with team search.
 *
 * The list of teams is fetched once per business-service input and filtered
 * locally on every keystroke – the team API does not expose a server-side
 * search parameter, so an in-memory filter is cheaper, instant, and avoids
 * the flicker caused by re-fetching the same page on every input.
 */
@Component({
    selector: 'sh-team-picker',
    templateUrl: './sh-team-picker.component.html',
    standalone: true,
    imports: [CommonModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ShTeamPickerComponent),
            multi: true
        }
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ShTeamPickerComponent implements ControlValueAccessor {
    @ViewChild('dropdown', { static: false }) public dropdownElement: ElementRef<HTMLElement> | undefined;

    private readonly teamService = inject(TeamService);

    // Inputs
    public label: InputSignal<string> = input<string>('Team');
    public disabled: InputSignal<boolean> = input<boolean>(false);
    public mandatory: InputSignal<boolean> = input<boolean>(false);
    public error: InputSignal<boolean> = input<boolean>(false);
    public placeholder: InputSignal<string> = input<string>('Search for team...');
    public businessService: InputSignal<BusinessServices | undefined> = input<BusinessServices | undefined>(undefined);

    // Internal state
    protected currentValue: string | null = null; // Team ID
    protected selectedTeam: WritableSignal<ITeam | null> = signal(null);
    protected isLoading: WritableSignal<boolean> = signal(false);
    protected isLoadingValue: WritableSignal<boolean> = signal(false);
    protected isDisabled: WritableSignal<boolean> = signal(false);
    protected searchTerm: WritableSignal<string> = signal('');
    protected isSearching: WritableSignal<boolean> = signal(false);

    /** Full list of teams for the current business-service filter. */
    private allTeams: WritableSignal<ITeam[]> = signal([]);

    /**
     * Tracks the most recent value we propagated upwards via `onChange`. While
     * set, any incoming `writeValue` call whose value does not match is
     * treated as a stale echo and ignored. This prevents the picker from
     * "snapping back" to a previously-selected team when the parent form is
     * slow to acknowledge a local clear/select action.
     */
    private pendingValue: string | null | undefined = undefined;

    /** Locally filtered search results. */
    protected searchResults: Signal<ITeam[]> = computed(() => {
        const term = this.searchTerm().trim().toLowerCase();
        const teams = this.allTeams();
        if (!term) {
            return teams;
        }
        return teams.filter((team) => (team.name ?? '').toLowerCase().includes(term));
    });

    private onChange: (value: string | null) => void = () => {};

    private onTouched: () => void = () => {};

    constructor() {
        // Load selected team when the form-control value changes externally.
        effect(() => {
            if (this.currentValue && !this.selectedTeam()) {
                this.loadTeamById(this.currentValue);
            }
        });

        // (Re)load the team list whenever the business-service input changes.
        effect(() => {
            // Read input so the effect re-executes when it changes.
            this.businessService();
            this.loadAllTeams();
        });
    }

    // ControlValueAccessor implementation
    public writeValue(value: string | null): void {
        // Don't overwrite state while the user is actively searching.
        if (this.isSearching()) {
            return;
        }
        // If we have a pending local change, only accept the matching ack;
        // ignore stale echoes that would otherwise snap the picker back to
        // a previously-selected team.
        if (this.pendingValue !== undefined) {
            if (value === this.pendingValue) {
                this.pendingValue = undefined;
            } else {
                return;
            }
        }
        if (value === this.currentValue) {
            return;
        }
        this.currentValue = value;
        if (value) {
            this.loadTeamById(value);
        } else {
            this.selectedTeam.set(null);
        }
    }

    public registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }

    /**
     * Load the full list of teams for the active business-service filter.
     */
    private loadAllTeams(): void {
        this.isLoading.set(true);
        this.teamService
            .getTeams(0, 100, this.businessService())
            .pipe(
                tap((result) => {
                    this.allTeams.set(result.items || []);
                }),
                catchError((error) => {
                    console.error('Error loading teams:', error);
                    this.allTeams.set([]);
                    return of({ items: [], total: 0, page: 0, pageSize: 0 });
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe();
    }

    /**
     * Handle search input change. Filtering happens locally via `searchResults`.
     *
     * While editing, the selected team is not re-rendered as an active item,
     * so the input value stays under the user's control – it will only be
     * restored on overlay close if no valid selection was made.
     */
    protected handleSearchInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const term = input.value;
        this.searchTerm.set(term);
        this.isSearching.set(true);
    }

    /**
     * Called when the dropdown's `active` property changes. Fires on the
     * dropdown itself when the overlay opens or closes. Menu-item
     * `active-changed` events also bubble through here, so we filter them
     * out via `event.target`.
     *
     * When the dropdown closes while the user was editing without
     * committing a valid selection, revert the input back to the previously
     * selected team – standard autocomplete UX.
     */
    protected handleActiveChanged(event: Event): void {
        const dropdown = this.dropdownElement?.nativeElement as (HTMLElement & { active?: boolean }) | undefined;
        if (!dropdown || event.target !== dropdown) {
            return;
        }
        if (dropdown.active) {
            return;
        }
        if (!this.isSearching()) {
            return;
        }
        this.searchTerm.set('');
        this.isSearching.set(false);
        const team = this.selectedTeam();
        this.setDropdownDisplayValue(team ? this.getTeamDisplayName(team) : '');
    }

    /**
     * Imperatively set the text shown in the SHUI dropdown's input.
     */
    private setDropdownDisplayValue(text: string): void {
        const el = this.dropdownElement?.nativeElement as (HTMLElement & { value?: string }) | undefined;
        if (el) {
            el.value = text;
        }
    }

    /**
     * Handle team selection from dropdown.
     */
    protected handleTeamSelect(team: ITeam): void {
        this.currentValue = team.id;
        this.selectedTeam.set(team);
        this.searchTerm.set('');
        this.isSearching.set(false);
        this.pendingValue = team.id;
        this.onChange(team.id);
        this.onTouched();
    }

    /**
     * Handle clear selection (clicking the active item).
     */
    protected handleClear(): void {
        this.currentValue = null;
        this.selectedTeam.set(null);
        this.searchTerm.set('');
        this.isSearching.set(false);
        this.pendingValue = null;
        this.onChange(null);
        this.onTouched();
    }

    /**
     * Load team details by ID.
     */
    private loadTeamById(id: string): void {
        this.isLoadingValue.set(true);
        this.teamService
            .getTeamById(id)
            .pipe(
                filter((team): team is ITeam => !!team),
                tap((team) => {
                    this.selectedTeam.set(team);
                }),
                catchError((error) => {
                    console.error('Error loading team:', error);
                    this.selectedTeam.set(null);
                    return of(null);
                }),
                finalize(() => this.isLoadingValue.set(false))
            )
            .subscribe();
    }

    /**
     * Get display name for a team.
     */
    protected getTeamDisplayName(team: ITeam | null): string {
        if (!team) {
            return '';
        }
        return team.name || team.id;
    }

    /**
     * Check if a team is currently selected.
     */
    protected isTeamActive(teamId: string): boolean {
        return this.currentValue === teamId;
    }
}
